import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MorePopOverMenu } from "../../../components/dropdown/moreMenuPopover";
import { Icon } from "../../../components/icons/icon";
import { TabNav } from "../../../components/navs/tabNav";
import { ContributionsApprovalTable } from "../../../components/table/app_tables/contributions";
import { CooperativeWithdrawalsTable } from "../../../components/table/app_tables/cooperativeWithdrawal";
import { DepositsApprovalTable } from "../../../components/table/app_tables/deposits";
import { LoanRepaymentsApprovalTable } from "../../../components/table/app_tables/loan repayments";
import { LoansApprovalTable } from "../../../components/table/app_tables/loans";
import { MembershipExitTable } from "../../../components/table/app_tables/memberExit";
import { MembershipApprovalTable } from "../../../components/table/app_tables/membership";
import { SharesApprovalTable } from "../../../components/table/app_tables/shares";
import { TableComponent } from "../../../components/table/table";
import { usePermissions } from "../../../customHooks/usePermissions";
import {
  useFetchAllApprovalsSummaryQuery,
  useFetchSingleCoopApprovalsSummaryQuery,
  useGetMembershipProfileQuery,
} from "../../../redux/features/cooperative/cooperative.api";
import { PermissionCode } from "../../../redux/features/settings/settings.type";
import { useAppSelector } from "../../../redux/hooks";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../utils/constants";

type Tabs =
  | "membership"
  | "contributions"
  | "deposits"
  | "shares"
  | "loans"
  | "loan_repayments"
  | "cooperative_withdrawals"
  | "member_exit";

type Nav = {
  title: string;
  to: Tabs;
  onClick: () => void;
  hasCount: boolean;
  count: number;
};

export const SingleCooperativeApproval = ({
  pageScrollProgress = 0,
}: {
  pageScrollProgress?: number;
}) => {
  const [tab, setTab] = useState<Tabs>();
  const [navs, setNavs] = useState<Nav[]>([]);

  const { id } = useParams<{ id: string }>();

  const { profile } = useAppSelector((state) => state.auth);

  const [searchParams, setSearchParams] = useSearchParams();

  const { hasPermissionsFor } = usePermissions();

  const { data: membershipProfile, isFetching: fetchingMembershipProfile } =
    useGetMembershipProfileQuery(
      {
        coop: `${id}`,
        Permission: true,
        collection: true,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !id,
      }
    );

  const {
    data: cooperativeApprovalSummary,
    isLoading: fetchingCooperativeApprovalSummary,
    refetch: refetchCooperativeApprovalSummary,
  } = useFetchSingleCoopApprovalsSummaryQuery(
    {
      coop: `${id}`,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  const { refetch: refetchAllApprovalSummary } =
    useFetchAllApprovalsSummaryQuery(undefined, {
      refetchOnMountOrArgChange: true,
      skip: profile?.type !== "member",
    });

  useEffect(() => {
    const userPermissions =
      membershipProfile?.data?.Role?.permissions?.map((p) => p.CodeName) ?? [];

    const pageTabs = (
      [
        {
          title: "Contribution",
          to: "contributions",
          onClick: () => {
            setSearchParams({ view: "contributions" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.paymentApproval ?? 0,
          permissionsRequired: ["can.add.payment", "can.approve.payment"],
        },
        {
          title: "Deposit",
          to: "deposits",
          onClick: () => {
            setSearchParams({ view: "deposits" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.paymentDepositApproval ?? 0,
          permissionsRequired: ["can.add.payment", "can.approve.payment"],
        },
        {
          title: "Share",
          to: "shares",
          onClick: () => {
            setSearchParams({ view: "shares" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.paymentSharesApproval ?? 0,
          permissionsRequired: ["can.add.payment", "can.approve.payment"],
        },
        {
          title: "Loan Request",
          to: "loans",
          onClick: () => {
            setSearchParams({ view: "loans" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.loanApproval ?? 0,
          permissionsRequired: ["can.approve.loans"],
        },
        {
          title: "Loan Repayment",
          to: "loan_repayments",
          onClick: () => {
            setSearchParams({ view: "loan_repayments" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.loanRepaymentApproval ?? 0,
          permissionsRequired: ["can.add.payment", "can.approve.payment"],
        },
        {
          title: "Membership",
          to: "membership",
          onClick: () => {
            setSearchParams({ view: "membership" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.memberApproval ?? 0,
          permissionsRequired: ["can.add.member", "can.approve.member"],
        },
        {
          title: "Member Exit",
          to: "member_exit",
          onClick: () => {
            setSearchParams({ view: "member_exit" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.memberRemoval ?? 0,
          permissionsRequired: ["can.remove.member", "can.approve.removal"],
        },
        {
          title: "Cooperative Withdrawal",
          to: "cooperative_withdrawals",
          onClick: () => {
            setSearchParams({ view: "cooperative_withdrawals" });
          },
          hasCount: true,
          count: cooperativeApprovalSummary?.data?.withdrawalApproval ?? 0,
          permissionsRequired: ["can.add.payment", "can.approve.payment"],
        },
      ] as (Nav & { permissionsRequired: PermissionCode[] })[]
    ).filter((t) =>
      hasPermissionsFor(t.permissionsRequired, userPermissions, "some")
    );

    // pageTabs.sort((a, b) => b.count - a.count);

    setNavs(pageTabs);
    // eslint-disable-next-line
  }, [cooperativeApprovalSummary, membershipProfile]);

  useEffect(() => {
    if (!!searchParams.get("view")) {
      setTab(searchParams.get("view") as Tabs);
    } else if (!searchParams.get("view") && !!navs[0]?.to) {
      setTab(navs[0].to);
    }
  }, [searchParams, navs]);

  return fetchingMembershipProfile || fetchingCooperativeApprovalSummary ? (
    <div className="flex flex-col gap-6 max-md:app-body-px max-md:pt-6">
      <Skeleton
        width={"100%"}
        height={"24px"}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
        className="max-w-full"
        containerClassName="flex-1"
      />

      <Skeleton
        width={"100%"}
        height={"32px"}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
        containerClassName="flex-1"
      />

      <TableComponent rows={[]} headers={[" ", " ", " ", " ", " "]} isLoading />
    </div>
  ) : (
    <div className="flex flex-col md:gap-6 max-md:pt-6">
      <div className="max-md:app-body-px flex items-center gap-4 justify-between">
        <div
          className="
          text-pry-green-200 font-medium text-base
          flex flex-row flex-wrap gap-1 items-center
          max-md:hidden
          "
        >
          <Link to="/app/approvals" className="text-pry-dark-green-300/50">
            Approvals
          </Link>

          <Icon
            svg="left-caret-icon"
            width={24}
            height={24}
            className="rotate-180"
          />

          <h1>{membershipProfile?.data?.Cooperative?.CoopName}</h1>
        </div>

        <MorePopOverMenu
          options={navs.map((t) => ({
            text: t.title,
            onClick: () => {
              t.onClick();
            },
            count: t.count,
          }))}
          className="w-full md:w-fit"
        >
          <button
            type="button"
            className="px-3.5 py-2.5 flex items-center gap-2 w-full bg-white border rounded-lg shadow-sm"
          >
            <p className="flex-1 text-left capitalize font-medium text-base">
              {navs.find((n) => n.to === tab)?.title} Approvals
            </p>

            <Icon svg="caret-down" width={20} height={20} />
          </button>
        </MorePopOverMenu>
      </div>

      <div className="hidden">
        <TabNav
          navs={navs?.map((t) => ({ ...t, count: undefined }))}
          className="md:mt-2"
          activeTab={tab ?? ""}
          variant="switcher"
          theme="white"
        />
      </div>

      {tab === "contributions" &&
      navs.map((n) => n.to).includes("contributions") &&
      !!membershipProfile?.data?.Cooperative ? (
        <ContributionsApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "deposits" &&
      navs.map((n) => n.to).includes("deposits") &&
      !!membershipProfile?.data?.Cooperative ? (
        <DepositsApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "shares" &&
      navs.map((n) => n.to).includes("shares") &&
      !!membershipProfile?.data?.Cooperative ? (
        <SharesApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "membership" &&
      navs.map((n) => n.to).includes("membership") &&
      !!membershipProfile?.data?.Cooperative ? (
        <MembershipApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "loans" &&
      navs.map((n) => n.to).includes("loans") &&
      !!membershipProfile?.data?.Cooperative ? (
        <LoansApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "loan_repayments" &&
      navs.map((n) => n.to).includes("loan_repayments") &&
      !!membershipProfile?.data?.Cooperative ? (
        <LoanRepaymentsApprovalTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "cooperative_withdrawals" &&
      navs.map((n) => n.to).includes("cooperative_withdrawals") &&
      !!membershipProfile?.data?.Cooperative ? (
        <CooperativeWithdrawalsTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}

      {tab === "member_exit" &&
      navs.map((n) => n.to).includes("member_exit") &&
      !!membershipProfile?.data?.Cooperative ? (
        <MembershipExitTable
          singleCooperative={membershipProfile.data.Cooperative}
          refresh={() => {
            refetchCooperativeApprovalSummary();
            refetchAllApprovalSummary();
          }}
          pageScrollProgress={pageScrollProgress}
        />
      ) : undefined}
    </div>
  );
};
